/**
 * What the current page is called, for a pin.
 *
 * The sidebar publishes every route it renders (path -> label + the module it
 * sits under) here, so a pin made from the header gets the same name the nav
 * gives the page. When the sidebar has not published the route (the classic
 * sidebar does not, and detail pages are not nav rows) the label falls back to
 * the page's own heading, then the active nav row, then the path itself.
 */
import { detectProductFromPath, PRODUCTS } from '../layout/Sidebar/productConfig';
import { normalizePinPath } from './usePins';

let navIndex = new Map();

/**
 * Replace the published index. `entries` is an iterable of
 * [path, { label, module }] (a Map works).
 */
export const publishNavIndex = (entries) => {
  const next = new Map();
  if (entries) {
    for (const [path, meta] of entries) {
      const key = normalizePinPath(path);
      if (key && meta && meta.label && !next.has(key)) {
        next.set(key, { label: String(meta.label), module: meta.module ? String(meta.module) : '' });
      }
    }
  }
  navIndex = next;
};

/** Exact match on path+search first, then on the pathname alone. */
export const lookupNavEntry = (path) => {
  const key = normalizePinPath(path);
  if (!key) return null;
  if (navIndex.has(key)) return navIndex.get(key);
  const pathname = key.split('?')[0];
  return navIndex.get(pathname) || null;
};

const SECTION_FALLBACKS = [
  ['/security-atlas', 'Security'],
  ['/security', 'Security'],
  ['/admin', 'Management'],
  ['/v2/admin', 'Management'],
];

const moduleForPath = (pathname) => {
  const productId = detectProductFromPath(pathname);
  const product = productId && PRODUCTS.find((p) => p.id === productId);
  if (product) return product.label;
  const hit = SECTION_FALLBACKS.find(([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`));
  return hit ? hit[1] : '';
};

const textOf = (selector) => {
  try {
    const el = document.querySelector(selector);
    const text = el && el.textContent ? el.textContent.replace(/\s+/g, ' ').trim() : '';
    return text && text.length <= 80 ? text : '';
  } catch {
    return '';
  }
};

const humanize = (pathname) => {
  const last = pathname.split('/').filter(Boolean).pop() || '';
  const words = decodeURIComponent(last).replace(/[-_]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Home';
};

/** { label, module } for a path, using the best source available. */
export const describePath = (path) => {
  const key = normalizePinPath(path);
  const pathname = key.split('?')[0] || '/';
  const entry = lookupNavEntry(key);
  if (entry) return { label: entry.label, module: entry.module || moduleForPath(pathname) };
  const label = textOf('.main-content h1')
    || textOf('.sidebar .nav-link.active .nav-link-label')
    || textOf('.sidebar .nav-link.active span')
    || humanize(pathname);
  return { label, module: moduleForPath(pathname) };
};
